import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
import type { SectionConfig } from '$global/utils/onwardClick/types';

/**
 * The heni.com route map for `onward_click` (HB-967).
 *
 * Lives here rather than in `global-v5` because these are this site's sections:
 * the eight other workspaces sharing that package have entirely different ones.
 *
 * Built from `GlobalRoutes` so a renamed route cannot silently stop resolving,
 * with only the entries that are genuinely a *section* — `GlobalRoutes` also
 * holds non-section routes (Privacy, Search, Jobs) which stay unmapped and fall
 * through to `other`.
 */
export const SECTION_CONFIG: SectionConfig = {
	internalHostnames: ['heni.com'],
	sectionPrefixes: [
		{ prefix: GlobalRoutes.NewsHome, section: 'news' },
		{ prefix: '/blog', section: 'news' },
		{ prefix: GlobalRoutes.ArtistsHome, section: 'artists' },
		{ prefix: GlobalRoutes.ArtworksHome, section: 'artworks' },
		{ prefix: '/works', section: 'artworks' },
		{ prefix: GlobalRoutes.EditionsHome, section: 'editions' },
		// Drops are the Editions/Primary release surface; `/drops` and
		// `/live-drops` both land on a release, so they read as Editions.
		{ prefix: GlobalRoutes.DropsHome, section: 'artworks' },
		{ prefix: GlobalRoutes.LiveDrops, section: 'artworks' },
		{ prefix: GlobalRoutes.PrimaryHome, section: 'primary' },
		{ prefix: GlobalRoutes.PrivateSalesHome, section: 'private-sales' },
		{ prefix: GlobalRoutes.ShopHome, section: 'shop' },
		{ prefix: GlobalRoutes.MerchHome, section: 'merch' },
		{ prefix: GlobalRoutes.ExhibitionsHome, section: 'exhibitions' },
		{ prefix: GlobalRoutes.TalksHome, section: 'talks' },
		{ prefix: GlobalRoutes.NftHome, section: 'nft' },
		{ prefix: GlobalRoutes.PublishingHome, section: 'publishing' },
		{ prefix: GlobalRoutes.DigitalHome, section: 'digital' },
		{ prefix: GlobalRoutes.GalleryHome, section: 'gallery' },
		// Last by construction: `/` prefixes every path, and the resolver matches
		// longest-first, so this only wins when nothing else does.
		{ prefix: GlobalRoutes.Home, section: 'home' },
	],
	/**
	 * The target type a single-slug-deep page represents in each section, so
	 * `/artists/damien-hirst` reads as an artist and `/editions/the-currency` as
	 * an artwork. Sections without an entry fall through to `other`.
	 */
	detailTargets: {
		artists: 'artist',
		artworks: 'artwork',
		editions: 'artwork',
		primary: 'artwork',
		shop: 'product',
		merch: 'artwork',
		news: 'article',
		exhibitions: 'exhibition',
	},
};
